import React, { useState, useEffect } from 'react';
import styles from './NewsInsights.module.css';
import Footer from '../shared/Footer';
import IndustryInsights from './IndustryInsights';

// ===== HARDCODED DATA (FALLBACK) =====
const DEFAULT_NEWS = {
  newsletter: {
    title: "Quarterly Newsletter – August 2025",
    source: "Newsletter Q3",
    pdfPath: "/images/news/Newsletter V3.pdf",
    highlights: [
      "Highlights from Q2: OpsAssist Lite launch, strategic partnerships, and operational wins.",
      "Focus on AI-driven airside operations and customer success stories.",
      "Teaser for upcoming product enhancements and regional expansion."
    ]
  },
  awards: [
    {
      title: "HKAI Lab Jumpstarter Showcase – 2025",
      description: "Selected participant in Hong Kong's startup showcase demonstrating OpsAssist Lite's real-time capabilities."
    },
    {
      title: "FTE APAC Innovate Awards Finalist – 2024",
      description: "Acknowledged for transforming ground operations with AI."
    },
    {
      title: "IMDA AI Award – 2021",
      description: "Early recognition for foundational AI work in aviation logistics."
    }
  ],
  pressReleases: [
    {
      title: "GHI Release – October 2025",
      source: "GHI Publication",
      link: "https://ghi.mydigitalpublication.co.uk/october-2025/page-34",
      description: "Featured in Ground Handling International (GHI), the collaboration between SATS and Neural Lab showcases AI-driven real-time task management at Changi Airport. The OpsAssist platform enhances resource allocation and boosts productivity through intelligent scheduling and live operational insights."
    },
    {
      title: "OpsAssist Lite Launch – November 2025",
      description: "Official launch of OpsAssist Lite, designed for dynamic resource deployment in airport ground operations. Targeted at operators with lean models, offering real-time visibility and planning optimization. Positioned to reduce idle time and boost productivity during demand surges."
    },
    {
      title: "SATS Collaboration Feature – Mingpao Review",
      source: "Ming Pao",
      link: "https://finance.mingpao.com/fin/dailyb1591166258391/20241220/1734668845706/%e5%88%9d%e5%89%b5%e8%90%bd%e5%9c%b0%e6%8b%8d%e4%bd%8f%e4%b8%8a%e7%b3%bb%e5%88%97-br-%e6%9c%ac%e5%9c%b0ai%e7%a7%91%e4%bc%81%e6%94%be%e7%9c%bc%e8%88%aa%e7%a9%ba%e6%94%b9%e9%9d%a9-%e5%8a%a9%e6%a8%9f%e5%ae%9c%e6%a9%9f%e5%a0%b4%e8%a1%8c%e6%9d%8e%e8%a3%9d%e5%8d%b8%e6%95%88%e7%8e%87%e5%a2%9e%e9%80%be20#goog_rewarded",
      description: "Media coverage of Neural Lab's partnership with SATS. Emphasis on AI's role in transforming airside logistics and operational efficiency."
    }
  ]
};

// ===== DATA LOADING =====
async function loadNewsData() {
  try {
    const response = await fetch('/content/news/index.json');
    if (!response.ok) {
      throw new Error('Failed to load news data');
    }
    const cmsData = await response.json();
    
    // Merge CMS data with default data
    const mergedData = {
      newsletter: cmsData.newsletter || DEFAULT_NEWS.newsletter,
      awards: [...DEFAULT_NEWS.awards, ...(cmsData.awards || [])],
      events: cmsData.events || [],
      pressReleases: [...DEFAULT_NEWS.pressReleases, ...(cmsData.pressReleases || [])]
    };
    
    return mergedData;
  } catch (error) {
    console.error('Error loading news data, using default:', error);
    return DEFAULT_NEWS;
  }
}

// ===== MAIN COMPONENT =====
function NewsInsights() {
  // Tab state management for switching between Company and Industry views
  const [activeTab, setActiveTab] = useState('company');
  const [newsData, setNewsData] = useState(DEFAULT_NEWS);
  const [loading, setLoading] = useState(true);

  // Load news data on component mount
  useEffect(() => {
    loadNewsData().then(data => {
      setNewsData(data);
      setLoading(false);
    });
  }, []);

  const renderCompanyNews = () => (
    <div className={styles.companyNews}>
      {loading ? (
        <div className={styles.loading}>Loading news...</div>
      ) : (
        <>
          {/* Newsletter Section */}
          {newsData.newsletter && (
            <div className={`${styles.newsSection} ${styles.newsSectionWhite}`}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionIcon}>🗞️</span>
                <h3 className={styles.sectionTitle}>Company News</h3>
              </div>
              <div className={styles.newsletterCard}>
                <h4 className={styles.newsletterTitle}>{newsData.newsletter.title}</h4>
                <p className={styles.newsletterSource}>
                  Source: <a href={process.env.PUBLIC_URL + newsData.newsletter.pdfPath} download className={styles.downloadLink}>{newsData.newsletter.source}</a>
                </p>
                <ul className={styles.newsletterHighlights}>
                  {newsData.newsletter.highlights.map((highlight, index) => (
                    <li key={index} className={styles.highlightItem}>{highlight}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Awards Section */}
          {newsData.awards.length > 0 && (
            <div className={`${styles.newsSection} ${styles.newsSectionBlue}`}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionIcon}>🏆</span>
                <h3 className={styles.sectionTitle}>Awards & Recognition</h3>
              </div>
              <div className={styles.awardsGrid}>
                {newsData.awards.map((award, index) => (
                  <div key={index} className={styles.awardCard}>
                    <h4 className={styles.awardTitle}>{award.title}</h4>
                    {award.source && (
                      <p className={styles.awardSource}>
                        Source: {award.link ? (
                          <a href={award.link} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                            {award.source}
                          </a>
                        ) : (
                          award.source
                        )}
                      </p>
                    )}
                    <p className={styles.awardDescription}>{award.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Events & Recognition Images */}
          <div className={`${styles.photoPlaceholders} ${styles.newsSectionWhite}`}>
            <h4 className={styles.placeholderTitle}>Events & Recognitions</h4>
            <p className={styles.placeholderText}>A collection of photos from award ceremonies, showcases, and milestone events</p>
            <div className={styles.photoGrid}>
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <div key={num} className={styles.photoPlaceholder}>
                  <img 
                    src={process.env.PUBLIC_URL + `/images/news/news${num}.JPG`} 
                    alt={`Event Photo ${num}`} 
                    className={styles.awardPhoto}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Press Releases Section */}
          {newsData.pressReleases.length > 0 && (
            <div className={`${styles.newsSection} ${styles.newsSectionBlue}`}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionIcon}>📣</span>
                <h3 className={styles.sectionTitle}>Press Releases</h3>
              </div>
              <div className={styles.pressReleases}>
                {newsData.pressReleases.map((release, index) => (
                  <div key={index} className={styles.pressReleaseCard}>
                    <h4 className={styles.pressReleaseTitle}>{release.title}</h4>
                    {release.source && release.link && (
                      <p className={styles.pressReleaseSource}>
                        Source: <a href={release.link} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                          {release.source}
                        </a>
                      </p>
                    )}
                    <p className={styles.pressReleaseDescription}>{release.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderIndustryInsights = () => <IndustryInsights />;

  return (
    <div className={styles.section}>
      <div 
        className={styles.hero}
        style={{ 
          backgroundImage: `url(${process.env.PUBLIC_URL + '/images/news/News-Insights.JPG'})` 
        }}
      >
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <h1 className={styles.title}>News & Insights</h1>
          <p className={styles.subtitle}>
            Stay updated with Neural Lab's latest breakthroughs, media features, and industry recognition.
          </p>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'company' ? styles.active : ''}`}
            onClick={() => setActiveTab('company')}
          >
            Company News
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'industry' ? styles.active : ''}`}
            onClick={() => setActiveTab('industry')}
          >
            Industry Insights
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === 'company' ? renderCompanyNews() : renderIndustryInsights()}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default NewsInsights;
